import { motion } from "framer-motion";

const domains = [
  "Self-Awareness",
  "Emotional Intelligence",
  "Confidence",
  "Resilience",
  "Decision Making",
  "Influence",
  "Self-Worth",
  "Purpose",
];

export default function Assessment() {
  return (
    <section
      id="assessment"
      className="py-24 md:py-40 relative overflow-hidden"
      style={{ background: "var(--sage)" }}
      data-testid="assessment-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-8">
            <span className="font-mono-editorial text-[11px] text-[color:var(--ink)]/70">Assessment</span>
            <h2 className="mt-6 font-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight text-[color:var(--ink)]">
              Not a test.
              <br />
              <em className="font-serif-editorial italic" style={{ color: "var(--brand)" }}>
                A quiet measure of who your child is becoming.
              </em>
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 self-end text-[color:var(--ink)]/80 leading-relaxed">
            Understanding your child&rsquo;s starting point — and measuring how far they have come. Age-adapted. No right or wrong answers. Results shared privately, never comparatively.
          </p>
        </div>

        {/* Two cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="border-2 p-8 md:p-10 paper-bg"
            style={{ borderColor: "var(--ink)" }}
            data-testid="assessment-pre"
          >
            <span className="font-mono-editorial text-[11px] tracking-[0.3em] text-[color:var(--muted)]">01 · Before the program</span>
            <h3 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02] tracking-tight text-[color:var(--ink)]">
              Pre-Assessment.
            </h3>
            <ul className="mt-6 space-y-3 text-[color:var(--ink)]/85">
              <li className="flex gap-3"><span style={{ color: "var(--brand)" }}>▸</span> Completed before the program begins</li>
              <li className="flex gap-3"><span style={{ color: "var(--brand)" }}>▸</span> Short questionnaire — <em>not</em> a test</li>
              <li className="flex gap-3"><span style={{ color: "var(--brand)" }}>▸</span> Helps Dr. Santosh understand where each child starts</li>
              <li className="flex gap-3"><span style={{ color: "var(--brand)" }}>▸</span> Format adapted by age</li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.08 }}
            className="border-2 p-8 md:p-10"
            style={{ borderColor: "var(--ink)", background: "var(--ink)", color: "#fff" }}
            data-testid="assessment-post"
          >
            <span className="font-mono-editorial text-[11px] tracking-[0.3em] text-white/60">02 · At year end</span>
            <h3 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
              Post-Assessment.
            </h3>
            <ul className="mt-6 space-y-3 text-white/85">
              <li className="flex gap-3"><span style={{ color: "var(--sunflower)" }}>▸</span> Same assessment repeated</li>
              <li className="flex gap-3"><span style={{ color: "var(--sunflower)" }}>▸</span> Growth measured across 8 pillars</li>
              <li className="flex gap-3"><span style={{ color: "var(--sunflower)" }}>▸</span> Shared <em>privately</em> with parents</li>
              <li className="flex gap-3"><span style={{ color: "var(--sunflower)" }}>▸</span> Guides each child&rsquo;s next phase of growth</li>
            </ul>
          </motion.div>
        </div>

        {/* 8 domain chips */}
        <div className="pt-10 border-t-2" style={{ borderColor: "var(--ink)" }}>
          <span className="font-mono-editorial text-[11px] text-[color:var(--ink)]/70">Growth measured across —</span>
          <div className="mt-6 flex flex-wrap gap-2 md:gap-3" data-testid="assessment-domains">
            {domains.map((d, i) => (
              <motion.span
                key={d}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="px-4 md:px-5 py-2 md:py-3 border-2 rounded-full font-serif-editorial italic text-lg md:text-xl transition-colors duration-300 cursor-default"
                style={{ borderColor: "var(--ink)" }}
                whileHover={{ background: "var(--ink)", color: "#fff" }}
              >
                {d}
              </motion.span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
